import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, filter, map, Subject } from 'rxjs';
import { endOfMonth, startOfMonth, subMonths } from 'date-fns';
import { FilterService } from '../../../../_shared/services';
import { WidgetFilterData } from '../../../../_shared/models';
import { ComplianceOverviewRequirementsRequest } from '../../compliance-overview/models/compliance-item.model';
import { ComplianceStandard } from '../../compliance-library/models/compliances.model';
import { CompliancesLibraryService } from '../../compliance-library/services/compliances-library.service';

export type CompliancePageFilterClearOptions = {
  accountIds?: boolean;
  assetType?: boolean;
  complianceType?: boolean;
  complianceRegion?: boolean;
  complianceStandard?: boolean;
  searchTerm?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class ComplianceStateService {
  private readonly compliancesLibraryService = inject(CompliancesLibraryService);
  private readonly filterService = inject(FilterService);
  private complianceRequestSubject = new BehaviorSubject<ComplianceOverviewRequirementsRequest | null>(null);
  private complianceStandardsSubject = new BehaviorSubject<ComplianceStandard[] | null>(null);
  private complianceStandardsAccountIds: number[] = [];
  private refreshSubject = new Subject<void>();

  request$ = this.complianceRequestSubject.asObservable().pipe(
    filter((request): request is ComplianceOverviewRequirementsRequest => request !== null),
    distinctUntilChanged(complianceRequestsEqual)
  );
  accountIds$ = this.request$.pipe(
    map(request => request.accountIds ?? []),
    distinctUntilChanged<number[]>(accountIdsEqual)
  );
  refresh$ = this.refreshSubject.asObservable();

  updateComplianceRequest(request: ComplianceOverviewRequirementsRequest): void {
    this.complianceRequestSubject.next({
      ...request,
      accountIds: request.accountIds ?? []
    });
  }

  getCurrentRequest(): ComplianceOverviewRequirementsRequest | null {
    return this.complianceRequestSubject.getValue();
  }

  /**
   * Clears filters that belong to a page context before child components
   * subscribe, avoiding an initial request with stale singleton state.
   */
  clearPageFilters(options: CompliancePageFilterClearOptions): void {
    const current = this.complianceRequestSubject.getValue();
    if (!current) {
      return;
    }

    const request: ComplianceOverviewRequirementsRequest = {
      ...current,
      accountIds: current.accountIds ?? []
    };
    let changed = false;

    if (options.accountIds && request.accountIds.length > 0) {
      request.accountIds = [];
      changed = true;
    }

    if (options.assetType && request.assetTypeId != null) {
      request.assetTypeId = undefined;
      changed = true;
    }

    if (options.complianceRegion && request.complianceRegion != null) {
      request.complianceRegion = undefined;
      changed = true;
    }

    if (options.complianceStandard && request.complianceId != null) {
      request.complianceId = undefined;
      changed = true;
    }

    if (options.complianceType && request.complianceType != null) {
      request.complianceType = undefined;
      changed = true;
    }

    if (options.searchTerm && request.searchTerm) {
      request.searchTerm = undefined;
      changed = true;
    }

    if (changed) {
      this.updateComplianceRequest(request);
    }

  }

  /**
   * Re-emits the widget filter for the request that is already in state,
   * without changing it. Used when data changes outside the filters (e.g. a
   * linked document is edited) and the KPI widgets need to reload against it.
   */
  syncWidgetFilter(): void {
    const request = this.complianceRequestSubject.getValue();
    if (!request) {
      return;
    }

    this.emitWidgetFilter(request);
  }

  refresh(): void {
    // Imports, creates, archives and restores change which standards exist, so the cached list can't be reused.
    this.complianceStandardsSubject.next(null);
    this.refreshSubject.next();
  }

  async getComplianceStandardsAsync(accountIds: number[], cancellation: Subject<void>): Promise<ComplianceStandard[]> {
    const cachedStandards = this.complianceStandardsSubject.getValue();
    if (cachedStandards && accountIdsEqual(accountIds, this.complianceStandardsAccountIds)) {
      return cachedStandards;
    }

    const response = await this.compliancesLibraryService.getComplianceStandardsAsync(accountIds, cancellation);
    const complianceStandards = response.data ?? [];
    this.complianceStandardsAccountIds = [...accountIds];
    this.complianceStandardsSubject.next(complianceStandards);
    return complianceStandards;
  }

  private emitWidgetFilter(request: ComplianceOverviewRequirementsRequest): void {
    const region = request.complianceRegion;
    const today = new Date();
    const startDate = startOfMonth(today);
    const endDate = endOfMonth(today);
    const previousCycleStartDate = startOfMonth(subMonths(startDate, 1));
    const previousCycleEndDate = endOfMonth(subMonths(endDate, 1));

    const filterData: WidgetFilterData = {
      accountIds: request.accountIds ?? [],
      regionId: region?.type === 'region' ? region.id : undefined,
      countryId: region?.type === 'country' ? region.id : undefined,
      complianceId: request.complianceId ?? undefined,
      assetTypeId: request.assetTypeId,
      startDate,
      endDate,
      previousCycleStartDate,
      previousCycleEndDate
    } as WidgetFilterData;

    this.filterService.updateFilter(filterData);
  }
}

function complianceRequestsEqual(
  a: ComplianceOverviewRequirementsRequest,
  b: ComplianceOverviewRequirementsRequest
): boolean {
  return a.searchTerm === b.searchTerm
    && a.page === b.page
    && a.pageSize === b.pageSize
    && a.complianceId === b.complianceId
    && a.assetTypeId === b.assetTypeId
    && a.complianceRegion?.id === b.complianceRegion?.id
    && a.complianceRegion?.type === b.complianceRegion?.type
    && a.complianceType?.id === b.complianceType?.id
    && accountIdsEqual(a.accountIds, b.accountIds);
}

function accountIdsEqual(a: number[] | undefined, b: number[] | undefined): boolean {
  if (a === b) return true;
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}